import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  BarChart3,
  Copy,
  Eye,
  FileInput,
  LayoutGrid,
  List,
  MoreHorizontal,
  PencilRuler,
  Plus,
  Sparkles,
  Star,
  Tags,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { adminApi } from "../../api/admin";
import { libraryApi, ROLE_LABEL, type LibraryItem } from "../../api/collab";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Menu,
  type MenuItem,
  PageHeader,
  SearchInput,
  Segmented,
  Skeleton,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import { RequestAccessCard } from "./RequestAccessCard";
import { designerUrl, listReportsFor, modeFor, reportUrl, type Mode } from "../../shell/role";
import type { Report } from "../../types";
import { reportStatus, screenCount } from "./reportStatus";
import { DesignScoreBadge } from "../../studio/DesignScore";
import { ReportPreviewThumb } from "../../components/ReportPreviewThumb";
import { NewReportDialog } from "./NewReportDialog";
import { ScreensBoard } from "./ScreensBoard";
import { ManageCategoriesDialog } from "./ManageCategoriesDialog";
import { CategoryTag, readStoredCategoryFilter, storeCategoryFilter, useReportCategories, type CategoryFilter } from "./reportCategories";

type ViewerTab = "all" | "shared" | "owned" | "favourites" | "recent";

type Filter = "all" | "draft" | "published" | "screens" | "attention" | "archived" | "offscreen";

function matchesFilter(r: Report, f: Filter): boolean {
  const s = reportStatus(r).kind;
  switch (f) {
    case "all":
      return r.status !== "archived" && r.status !== "deleted";
    case "draft":
      return s === "draft";
    case "published":
      return s === "published" || s === "screens";
    case "screens":
      return r.status !== "archived" && r.status !== "deleted" && screenCount(r) > 0;
    case "attention":
      return s === "attention";
    case "archived":
      return r.status === "archived";
    case "offscreen":
      return s === "published";
  }
}

/** Shown while the live preview loads (and if it can't): an abstract
 * mini-dashboard tinted per report so every card still reads as "a report". */
function ReportThumbPlaceholder({ r }: { r: Report }) {
  const hue = [...r.name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(135deg, hsl(${hue} 55% 22%), hsl(${(hue + 40) % 360} 60% 32%))`,
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", left: 16, top: 16, right: 16, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ height: 14, borderRadius: 3, background: "rgba(255,255,255,0.16)" }} />
        ))}
      </div>
      <div style={{ position: "absolute", left: 16, bottom: 12, display: "flex", alignItems: "flex-end", gap: 5 }}>
        {[22, 34, 18, 40, 28, 46, 30].map((h, i) => (
          <div key={i} style={{ width: 10, height: h, borderRadius: 2, background: "rgba(255,255,255,0.28)" }} />
        ))}
      </div>
      <div style={{ position: "absolute", right: 16, bottom: 12, width: 44, height: 44, borderRadius: "50%", border: "7px solid rgba(255,255,255,0.28)", borderRightColor: "rgba(255,255,255,0.6)" }} />
    </div>
  );
}

/** Live first-page preview: builders see the draft they're editing, viewers
 * the published version (the only one they can open). */
function ReportThumb({ r, mode }: { r: Report; mode: Mode }) {
  const source = mode === "viewer" ? "published" : mode === "owner" ? "owner" : "admin";
  return <ReportPreviewThumb reportId={r.id} source={source} title={r.name} placeholder={<ReportThumbPlaceholder r={r} />} />;
}

export function ReportsGalleryPage({ viewerOnly }: { viewerOnly?: boolean }) {
  const { user } = useAuth();
  const loc = useLocation();
  const urlMode: Mode = loc.pathname.startsWith("/owner") ? "owner" : loc.pathname.startsWith("/viewer") ? "viewer" : "admin";
  const mode: Mode = viewerOnly ? "viewer" : urlMode === "admin" && modeFor(user) !== "admin" ? modeFor(user) : urlMode;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>((params.get("filter") as Filter) || "all");
  const [view, setView] = useState<"cards" | "list">(() => {
    try {
      return (window.localStorage.getItem("mrp-reports-view") as "cards" | "list") || "cards";
    } catch {
      return "cards";
    }
  });
  const [creating, setCreating] = useState(params.get("new") === "1");
  const [createPath, setCreatePath] = useState<"layout" | "data" | undefined>(params.get("path") === "data" ? "data" : undefined);
  const [catChoice, setCatChoice] = useState<CategoryFilter>(readStoredCategoryFilter);
  const [managingCats, setManagingCats] = useState(false);
  const chooseCat = (v: CategoryFilter) => {
    setCatChoice(v);
    storeCategoryFilter(v);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem("mrp-reports-view", view);
    } catch {
      // storage blocked: the view just isn't remembered
    }
  }, [view]);

  const reports = useQuery({ queryKey: ["reports", mode], queryFn: () => listReportsFor(mode) });
  // v3.0 viewer library: owner, role, favourites, recents, data freshness (an older server just has none).
  const [vtab, setVtab] = useState<ViewerTab>("all");
  const library = useQuery({ queryKey: ["library"], queryFn: libraryApi.list, enabled: mode === "viewer", retry: false });
  const lib = useMemo(() => new Map<string, LibraryItem>((library.data ?? []).map((i) => [i.id, i])), [library.data]);
  const setFavourite = useMutation({
    mutationFn: (v: { id: string; on: boolean }) => libraryApi.setFavourite(v.id, v.on),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["library"] }),
    onError: (e) => toast.error("Couldn't update favourites", e instanceof Error ? e.message : undefined),
  });
  const inTab = (r: Report): boolean => {
    if (mode !== "viewer" || vtab === "all") return true;
    const i = lib.get(r.id);
    if (!i) return false;
    return vtab === "shared" ? i.shared_with_me : vtab === "owned" ? i.owned_by_me : vtab === "favourites" ? i.favourite : !!i.last_opened_at;
  };
  const tabCount = (t: ViewerTab) => (t === "all" ? (reports.data ?? []).length : (reports.data ?? []).filter((r) => {
    const i = lib.get(r.id);
    return !!i && (t === "shared" ? i.shared_with_me : t === "owned" ? i.owned_by_me : t === "favourites" ? i.favourite : !!i.last_opened_at);
  }).length);
  const all = reports.data ?? [];
  const { categories } = useReportCategories(mode, reports.data);
  const hasCategories = categories.length > 0;
  const knownCat = useMemo(() => new Set(categories.map((c) => c.id)), [categories]);
  /** The category a report is shown under (null = Uncategorized). */
  const catOf = (r: Report): string | null => (r.category_id && knownCat.has(r.category_id) ? r.category_id : null);
  // A remembered chip whose category was deleted (or that this user can't see) falls back to All.
  const activeCat: CategoryFilter = !hasCategories ? "all" : catChoice === "all" || catChoice === "none" || knownCat.has(catChoice) ? catChoice : "all";
  const activeCatName = categories.find((c) => c.id === activeCat)?.name;

  // Status filter + search first; the category chips count within that.
  const statusFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((r) => matchesFilter(r, filter))
      .filter(inTab)
      .filter((r) => !q || r.name.toLowerCase().includes(q) || (r.description ?? "").toLowerCase().includes(q) || (r.owner_name ?? "").toLowerCase().includes(q) || (r.category_name ?? "").toLowerCase().includes(q))
      .sort((a, b) => (mode === "viewer" && vtab === "recent" ? (lib.get(b.id)?.last_opened_at ?? "").localeCompare(lib.get(a.id)?.last_opened_at ?? "") : (b.updated_at ?? "").localeCompare(a.updated_at ?? "")));
  }, [all, filter, search, vtab, lib, mode]);
  const catCounts = useMemo(() => {
    const c = new Map<string, number>();
    for (const r of statusFiltered) {
      const k = r.category_id && knownCat.has(r.category_id) ? r.category_id : "none";
      c.set(k, (c.get(k) ?? 0) + 1);
    }
    return c;
  }, [statusFiltered, knownCat]);
  const filtered = activeCat === "all" ? statusFiltered : statusFiltered.filter((r) => (catOf(r) ?? "none") === activeCat);
  // "All" in card view: one section per category (TV menu order), Uncategorized last.
  const sections =
    hasCategories && activeCat === "all"
      ? [
          ...categories.map((c) => ({ key: c.id, title: c.name, items: filtered.filter((r) => catOf(r) === c.id) })),
          { key: "none", title: "Uncategorized", items: filtered.filter((r) => catOf(r) === null) },
        ].filter((sct) => sct.items.length > 0)
      : null;

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: 0, draft: 0, published: 0, screens: 0, attention: 0, archived: 0, offscreen: 0 };
    for (const r of all) (Object.keys(c) as Filter[]).forEach((f) => matchesFilter(r, f) && c[f]++);
    return c;
  }, [all]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["reports"] });
  const act = useMutation({
    mutationFn: async ({ kind, r }: { kind: "archive" | "restore" | "duplicate" | "delete"; r: Report }) => {
      if (kind === "archive") return adminApi.archiveReport(r.id);
      if (kind === "restore") return adminApi.restoreReport(r.id);
      if (kind === "duplicate") return adminApi.duplicateReport(r.id);
      return adminApi.deleteReport(r.id);
    },
    onSuccess: (_d, v) => {
      refresh();
      toast.success({ archive: "Report archived", restore: "Report restored", duplicate: "Copy created", delete: "Report deleted" }[v.kind], v.r.name);
    },
    onError: (e) => toast.error("That didn't work", e instanceof Error ? e.message : undefined),
  });

  const menuFor = (r: Report): MenuItem[] => {
    const items: MenuItem[] = [
      { label: "Open", icon: Eye, onSelect: () => navigate(reportUrl(mode, r.id)) },
      ...(mode !== "viewer" ? [{ label: "Edit in designer", icon: PencilRuler, onSelect: () => navigate(designerUrl(mode, r.id)) }] : []),
    ];
    if (mode === "admin") {
      items.push({ label: "Duplicate", icon: Copy, onSelect: () => act.mutate({ kind: "duplicate", r }) });
      if (r.status === "archived") items.push({ label: "Restore", icon: ArchiveRestore, onSelect: () => act.mutate({ kind: "restore", r }) });
      else
        items.push({
          label: "Archive",
          icon: Archive,
          onSelect: async () => {
            if (await confirm({ title: `Archive “${r.name}”?`, message: "It disappears from TVs and viewers. You can restore it any time.", confirmLabel: "Archive" })) act.mutate({ kind: "archive", r });
          },
        });
      items.push({
        label: "Delete…",
        icon: Trash2,
        danger: true,
        onSelect: async () => {
          if (await confirm({ title: `Delete “${r.name}”?`, message: "This removes the report, its drafts and its version history. This cannot be undone.", confirmLabel: "Delete report", tone: "danger" })) act.mutate({ kind: "delete", r });
        },
      });
    }
    return items;
  };

  const renderCard = (r: Report) => {
    const st = reportStatus(r);
    return (
      <Card key={r.id} onClick={() => navigate(reportUrl(mode, r.id))}>
        <ReportThumb r={r} mode={mode} />
        <div className="ds-row" style={{ alignItems: "flex-start" }}>
          <div className="ds-grow" style={{ minWidth: 0 }}>
            <div className="ds-truncate" style={{ fontWeight: 650, fontSize: 15 }}>{r.name}</div>
            <div className="ds-small ds-muted" style={{ marginTop: 4, height: 38, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {r.description || "No description yet."}
            </div>
          </div>
          {mode === "viewer" && lib.has(r.id) ? (
            <Button size="sm" variant="ghost" icon={Star} aria-label={lib.get(r.id)?.favourite ? "Remove from favourites" : "Add to favourites"} title={lib.get(r.id)?.favourite ? "Remove from favourites" : "Add to favourites"} style={lib.get(r.id)?.favourite ? { color: "var(--warning, #f59e0b)" } : undefined} onClick={(e) => { e.stopPropagation(); setFavourite.mutate({ id: r.id, on: !lib.get(r.id)?.favourite }); }} />
          ) : null}
          <Menu trigger={(p) => <Button {...p} size="sm" variant="ghost" icon={MoreHorizontal} aria-label="More actions" />} items={menuFor(r)} />
        </div>
        {mode === "viewer" && lib.has(r.id) ? <InfoLine item={lib.get(r.id) as LibraryItem} /> : null}
        <div className="ds-row" style={{ marginTop: 12, justifyContent: "space-between", gap: 8 }}>
          <span className="ds-row" style={{ gap: 6, minWidth: 0, flexWrap: "wrap" }}>
            {hasCategories ? <CategoryTag name={catOf(r) ? r.category_name : null} showNone={mode === "admin"} /> : null}
            <Badge tone={st.tone} dot title={st.hint}>{st.label}</Badge>
            {typeof r.design_score === "number" ? <DesignScoreBadge size="sm" score={r.design_score} /> : null}
          </span>
          <span className="ds-small ds-faint" style={{ whiteSpace: "nowrap" }}>
            {r.page_count ? `${r.page_count} pg · ` : ""}
            {formatRelative(r.updated_at)}
          </span>
        </div>
      </Card>
    );
  };

  const title = mode === "viewer" ? "Reports" : mode === "owner" ? "My reports" : "Reports";
  const subtitle =
    mode === "viewer"
      ? "Published dashboards you have access to."
      : "Every report has a draft you can safely edit. Nothing reaches the TVs until you publish.";

  return (
    <div className="ds-page">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          mode === "admin" ? (
            <>
              <Button icon={FileInput} onClick={() => navigate("/admin/import/powerbi")}>Import from Power BI</Button>
              <Button icon={Sparkles} onClick={() => { setCreatePath("data"); setCreating(true); }} title="Pick a dataset and get three suggested dashboards">Build from my data</Button>
              <Button variant="primary" icon={Plus} onClick={() => { setCreatePath("layout"); setCreating(true); }}>New report</Button>
            </>
          ) : mode === "owner" ? (
            <>
              <Button icon={Sparkles} onClick={() => { setCreatePath("data"); setCreating(true); }} title="Pick a table and get three suggested dashboards">Build from my data</Button>
              <Button variant="primary" icon={Plus} onClick={() => { setCreatePath("layout"); setCreating(true); }}>New report</Button>
            </>
          ) : null
        }
      />

      <div className="ds-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name, description or owner" width={320} />
        {mode === "viewer" && library.data ? (
          <Segmented<ViewerTab>
            value={vtab}
            onChange={setVtab}
            options={[
              { value: "all", label: `All ${tabCount("all")}` },
              { value: "shared", label: `Shared with me ${tabCount("shared")}` },
              { value: "owned", label: `Owned by me ${tabCount("owned")}` },
              { value: "favourites", label: `Favourites ${tabCount("favourites")}` },
              { value: "recent", label: `Recent ${tabCount("recent")}` },
            ]}
          />
        ) : null}
        {mode !== "viewer" ? (
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: `All ${counts.all}` },
              { value: "draft", label: `Drafts ${counts.draft}` },
              { value: "published", label: `Published ${counts.published}` },
              { value: "screens", label: `On screens ${counts.screens}` },
              ...(counts.attention ? [{ value: "attention" as Filter, label: `Needs attention ${counts.attention}` }] : []),
              { value: "archived", label: `Archived ${counts.archived}` },
            ]}
          />
        ) : null}
        <div className="ds-grow" />
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "cards", label: "", icon: LayoutGrid, title: "Card view" },
            { value: "list", label: "", icon: List, title: "List view" },
          ]}
        />
      </div>

      {hasCategories || mode === "admin" ? (
        <div className="ds-toolbar" style={{ marginTop: -4 }}>
          {hasCategories ? (
            <div className="ds-chips" role="group" aria-label="Filter by category">
              <span className="ds-small ds-faint" style={{ marginRight: 2 }}>Category</span>
              <CatChip active={activeCat === "all"} onClick={() => chooseCat("all")} label="All" count={statusFiltered.length} />
              {categories.map((c) => (
                <CatChip key={c.id} active={activeCat === c.id} onClick={() => chooseCat(c.id)} label={c.name} count={catCounts.get(c.id) ?? 0} />
              ))}
              <CatChip muted active={activeCat === "none"} onClick={() => chooseCat("none")} label="Uncategorized" count={catCounts.get("none") ?? 0} title="Reports not in any category yet" />
            </div>
          ) : (
            <span className="ds-small ds-muted">Group reports by company (for example MOP and MOS) so the TV Reports menu shows them in sections.</span>
          )}
          <div className="ds-grow" />
          {mode === "admin" ? (
            <Button size="sm" icon={Tags} onClick={() => setManagingCats(true)} title="Add, rename, reorder or delete categories">
              Categories
            </Button>
          ) : null}
        </div>
      ) : null}

      {reports.isLoading ? (
        <div className="ds-grid ds-grid--auto">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <Skeleton height={96} style={{ margin: "-16px -20px 14px", width: "calc(100% + 40px)" }} />
              <Skeleton width="70%" height={16} />
              <Skeleton width="90%" style={{ marginTop: 10 }} />
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          {all.length > 0 && activeCat !== "all" && statusFiltered.length > 0 ? (
            <EmptyState
              icon={Tags}
              title={activeCat === "none" ? "Every report here has a category" : `No reports in ${activeCatName ?? "this category"} yet`}
              description={activeCat === "none" ? "Nothing is uncategorized for this filter." : mode === "admin" ? `Open a report and choose ${activeCatName} under Category, or create a new report in it.` : "Try another category."}
              action={
                <span className="ds-row" style={{ gap: 8 }}>
                  <Button onClick={() => chooseCat("all")}>Show all categories</Button>
                  {mode === "admin" && activeCat !== "none" ? <Button variant="primary" icon={Plus} onClick={() => { setCreatePath("layout"); setCreating(true); }}>New report in {activeCatName}</Button> : null}
                </span>
              }
            />
          ) : (
            <EmptyState
              icon={BarChart3}
              title={all.length === 0 ? "No reports yet" : "Nothing matches"}
              description={all.length === 0 ? (mode === "admin" ? "Create your first report from a template or import one from Power BI." : mode === "owner" ? "Start one from a template, or pick a table and get suggested dashboards." : "When a report is shared with you it appears here.") : "Try another search or filter."}
              action={mode !== "viewer" && all.length === 0 ? <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>New report</Button> : undefined}
            />
          )}
        </Card>
      ) : view === "cards" && filter === "screens" ? (
        <ScreensBoard reports={filtered} mode={mode} />
      ) : view === "cards" ? (
        sections ? (
          <div>
            {sections.map((sct) => (
              <section key={sct.key} aria-labelledby={`cat-${sct.key}`}>
                <div className="ds-section-head">
                  <h2 id={`cat-${sct.key}`} className="ds-section-head__title" style={sct.key === "none" ? { color: "var(--text-muted)" } : undefined}>{sct.title}</h2>
                  <span className="ds-chip__count">{sct.items.length}</span>
                  {sct.key === "none" && mode === "admin" ? <span className="ds-small ds-faint">Not grouped on the TV Reports menu yet — open a report to pick its category.</span> : null}
                  <div className="ds-grow" />
                  {sct.items.length > 1 ? <Button size="sm" variant="ghost" onClick={() => chooseCat(sct.key)}>Only {sct.title}</Button> : null}
                </div>
                <div className="ds-grid ds-grid--auto">{sct.items.map((r) => renderCard(r))}</div>
              </section>
            ))}
          </div>
        ) : (
          <div className="ds-grid ds-grid--auto">{filtered.map((r) => renderCard(r))}</div>
        )
      ) : (
        <Card flush>
          <DataTable<Report>
            rows={filtered}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(reportUrl(mode, r.id))}
            initialSort={{ key: "updated", dir: "desc" }}
            columns={[
              { key: "name", header: "Report", sortValue: (r) => r.name, cell: (r) => <div><div style={{ fontWeight: 600 }}>{r.name}</div><div className="ds-muted ds-truncate" style={{ maxWidth: 480 }}>{r.description}</div></div> },
              ...(hasCategories ? [{ key: "category", header: "Category", sortValue: (r: Report) => (catOf(r) ? r.category_name ?? "" : "\uffff"), cell: (r: Report) => <CategoryTag name={catOf(r) ? r.category_name : null} showNone={mode === "admin"} /> }] : []),
              { key: "status", header: "Status", sortValue: (r) => reportStatus(r).label, cell: (r) => { const st = reportStatus(r); return <Badge tone={st.tone} dot title={st.hint}>{st.label}</Badge>; } },
              { key: "design", header: "Design", sortValue: (r) => r.design_score ?? -1, cell: (r) => (typeof r.design_score === "number" ? <DesignScoreBadge size="sm" score={r.design_score} /> : <span className="ds-faint">—</span>) },
              { key: "owner", header: "Owner", sortValue: (r) => r.owner_name ?? "", cell: (r) => <span className="ds-muted">{r.owner_name ?? "—"}</span> },
              { key: "pages", header: "Pages", align: "right", sortValue: (r) => r.page_count ?? 0, cell: (r) => r.page_count ?? "—" },
              { key: "updated", header: "Last edited", sortValue: (r) => r.updated_at, cell: (r) => <span className="ds-muted">{formatRelative(r.updated_at)}</span> },
              { key: "actions", header: "", width: 56, cell: (r) => <Menu trigger={(p) => <Button {...p} size="sm" variant="ghost" icon={MoreHorizontal} aria-label="More actions" />} items={menuFor(r)} /> },
            ]}
          />
        </Card>
      )}

      {mode === "viewer" ? <RequestAccessCard /> : null}
      {mode === "admin" ? <ManageCategoriesDialog open={managingCats} onClose={() => setManagingCats(false)} /> : null}
      <NewReportDialog
        mode={mode === "owner" ? "owner" : "admin"}
        open={creating}
        categories={mode === "admin" ? categories : undefined}
        initialCategoryId={activeCat !== "all" && activeCat !== "none" ? activeCat : null}
        initialTemplate={params.get("template")}
        initialPath={createPath}
        onClose={() => {
          setCreating(false);
          if (params.get("new") || params.get("template") || params.get("path")) {
            params.delete("new");
            params.delete("template");
            params.delete("path");
            setParams(params, { replace: true });
          }
        }}
        onCreated={(id) => {
          refresh();
          navigate(`${mode === "owner" ? "/owner" : "/admin"}/reports/${id}/edit`);
        }}
      />
    </div>
  );
}

/** Owner, role, last update and data freshness under a report card (viewer library). */
function InfoLine({ item }: { item: LibraryItem }) {
  const parts = [
    item.owner_name ? `Owner ${item.owner_name}` : null,
    item.shared_with_me && item.my_role !== "viewer" ? ROLE_LABEL[item.my_role] ?? null : null,
    item.last_published_at ? `Updated ${formatRelative(item.last_published_at)}` : "Not published yet",
    item.data_as_of ? `Data ${formatRelative(item.data_as_of)}` : null,
  ].filter(Boolean);
  return <div className="ds-small ds-faint" style={{ marginTop: 8 }}>{parts.join(" · ")}</div>;
}

function CatChip({ active, onClick, label, count, muted, title }: { active: boolean; onClick: () => void; label: string; count: number; muted?: boolean; title?: string }) {
  return (
    <button type="button" className={`ds-chip${muted ? " ds-chip--muted" : ""}`} aria-pressed={active} onClick={onClick} title={title}>
      {label}
      <span className="ds-chip__count">{count}</span>
    </button>
  );
}
